import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { initiateCspmScan, clearAllCspmMessages } from "../../../store/cspm/cspmSlice";
import { ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";

const ACCENT = "#F98125";

const CLOSE_X_STYLE = {
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  color: "#FFFFFF",
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "none",
  flexShrink: 0,
  WebkitTextStroke: "0.6px #FFFFFF",
};

const GREY_CLOSE_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  color: "#fff",
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#5a6268",
    borderColor: "#5a6268",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#e2e8f0",
    color: "#94a3b8",
    borderColor: "#e2e8f0",
  },
};

const PRIMARY_BTN_SX = {
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  backgroundColor: ACCENT,
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#e8741c",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#fed7aa",
    color: "#fff",
  },
};

const InitiateScanModal = ({ open, onClose, accountsWithWorkloads, selectedAccount, selectedWorkload, onSuccess }) => {
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const { initiateCspmScanMessage, initiateCspmScanError, isInitiatingCspmScan } = useSelector(state => state.cspm);

  // Find workload name for display and payload
  let workloadName, scheduleId, scheduleType;
  if (selectedWorkload) {
    let workloadObj = null;
    for (const acc of accountsWithWorkloads) {
      workloadObj = (acc.workloads || []).find(w => w.id === selectedWorkload) ||
                    (acc.schedules || []).find(s => s.scheduleId === selectedWorkload);
      if (workloadObj) break;
    }
    workloadName = workloadObj?.name || workloadObj?.scheduleName;
    scheduleId = workloadObj?.scheduleId;
    scheduleType = workloadObj?.scheduleType;
  }

  // Handle API responses
  useEffect(() => {
    if (initiateCspmScanMessage) {
      if (onSuccess) onSuccess(initiateCspmScanMessage);
      dispatch(clearAllCspmMessages());
      onClose();
    } else if (initiateCspmScanError) {
      setSnackbar({ open: true, message: initiateCspmScanError, severity: 'error' });
      dispatch(clearAllCspmMessages());
    }
    // eslint-disable-next-line
  }, [initiateCspmScanMessage, initiateCspmScanError]);

  useEffect(() => {
    if (!open) {
      setSnackbar({ open: false, message: '', severity: 'error' });
      dispatch(clearAllCspmMessages());
    }
    // eslint-disable-next-line
  }, [open]);

  const handleProceed = () => {
    const payload = {
      accountNumber: selectedAccount,
      ...(scheduleId && { scheduleId }),
      ...(scheduleType && { scheduleType }),
      ...(workloadName && { workloadName })
    };
    dispatch(initiateCspmScan(payload));
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  return (
    <>
      <Dialog
        open={open}
        onClose={isInitiatingCspmScan ? undefined : onClose}
        maxWidth="xs"
        fullWidth
        aria-labelledby="initiate-scan-modal"
        aria-describedby="modal-for-initiating-scan"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "none",
            border: "1px solid #e2e8f0",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: ACCENT,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id="initiate-scan-modal"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.125rem", sm: "1.25rem" },
                color: "#FFFFFF",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              Initiate Scan
            </Typography>
            <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem" }}>
              Confirm before starting a scan for this account.
            </Typography>
          </Box>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={isInitiatingCspmScan}
            style={{
              ...CLOSE_X_STYLE,
              cursor: isInitiatingCspmScan ? "not-allowed" : "pointer",
              opacity: isInitiatingCspmScan ? 0.6 : 1,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>

        <DialogContent
          id="modal-for-initiating-scan"
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            backgroundColor: "#f3f4f6",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              p: 2,
            }}
          >
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 500, color: "#111827", lineHeight: 1.5 }}>
              Are you sure you want to initiate a scan?
            </Typography>
            <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
              <Typography sx={{ fontSize: "0.875rem", color: "#334155" }}>
                <Box component="span" sx={{ fontWeight: 700, color: "#111827" }}>Account: </Box>
                {selectedAccount || "—"}
              </Typography>
              {workloadName ? (
                <Typography sx={{ fontSize: "0.875rem", color: "#334155" }}>
                  <Box component="span" sx={{ fontWeight: 700, color: "#111827" }}>Workload: </Box>
                  {workloadName}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #e2e8f0",
            bgcolor: "#fff",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="contained"
            disableElevation
            onClick={onClose}
            disabled={isInitiatingCspmScan}
            sx={GREY_CLOSE_BTN_SX}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleProceed}
            disabled={isInitiatingCspmScan}
            startIcon={isInitiatingCspmScan ? <CircularProgress size={16} color="inherit" /> : null}
            sx={PRIMARY_BTN_SX}
          >
            {isInitiatingCspmScan ? "Processing..." : "Proceed"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InitiateScanModal; 